import { Injectable, inject } from '@angular/core';
import { AutoclassifierSdkService } from '../autoclassifier-sdk/autoclassifier-sdk.service';
import { AuthService } from '../auth/auth.service';
import { FronteggAuthService } from '@frontegg/angular';

@Injectable({
  providedIn: 'root'
})
export class TranscriptMetadataService {
  private autoclassifierSdkService = inject(AutoclassifierSdkService);
  private authService = inject(AuthService);
  private fronteggAuthService = inject(FronteggAuthService);

  /**
   * Gets authenticated access token, throws if not available
   */
  private async getAccessToken(): Promise<string> {
    const token = await this.authService.waitForToken();
    if (!token) throw new Error('Not authenticated');
    return token;
  }

  /**
   * Saves extraction data for a transcript.
   * @param transcriptMetadata The metadata object containing extraction information for the transcript.
   */
  async saveExtraction(transcriptMetadata: any): Promise<any> {
    const accessToken = await this.getAccessToken();
    const payload = {
      saved_data: transcriptMetadata.SavedData,
      first_name: transcriptMetadata.FirstName,
      last_name: transcriptMetadata.LastName,
      student_id: transcriptMetadata.StudentId,
      institution_id: transcriptMetadata.InstitutionId
    };
    return this.autoclassifierSdkService.transcriptMetaDataSdk.saveExtraction(
      accessToken,
      transcriptMetadata.TransId,
      payload
    );
  }

  /**
   * Loads extraction data for a given transcript.
   * @param transcriptId The unique identifier of the transcript to load extraction data for.
   */
  async loadExtraction(transcriptId: string): Promise<any> {
    const accessToken = await this.getAccessToken();
    const result = await this.autoclassifierSdkService.transcriptMetaDataSdk.loadExtraction(
      accessToken,
      transcriptId
    );

    this.autoclassifierSdkService.transcriptMetaDataSdk
      .transcriptViewAudit(accessToken, transcriptId)
      .catch(err => console.warn('Failed to record view:', err));

    return result;
  }

  /**
   * Retrieves all documents from the transcript metadata service.
   */
  async getAllDocuments(): Promise<any[]> {
    const accessToken = await this.getAccessToken();
    return this.autoclassifierSdkService.transcriptMetaDataSdk.getAllDocuments(accessToken);
  }

  /**
   * Retrieves metadata for a specific document.
   * @param transcriptId The unique identifier of the transcript.
   */
  async getTranscriptMetadata(transcriptId: string): Promise<any> {
    const accessToken = await this.getAccessToken();
    return this.autoclassifierSdkService.transcriptMetaDataSdk.getTranscriptContent(
      accessToken,
      transcriptId
    );
  }

  /**
   * Deletes a document by its transcript ID.
   * @param transId The unique identifier of the transcript to delete.
   */
  async deleteDocument(transId: string): Promise<void> {
    const accessToken = await this.getAccessToken();
    return this.autoclassifierSdkService.transcriptMetaDataSdk.deleteDocument(accessToken, transId);
  }

  /**
   * Saves the status for a transcript.
   * @param transId The unique identifier of the transcript.
   * @param statusId The status identifier to be saved for the transcript.
   */
  async saveStatus(transId: string, statusId: number): Promise<any> {
    const accessToken = await this.getAccessToken();
    return this.autoclassifierSdkService.transcriptMetaDataSdk.saveStatus(
      accessToken,
      transId,
      statusId
    );
  }

  /**
   * Locks a transcript to prevent concurrent editing.
   * @param transId The unique identifier of the transcript to lock.
   * @param userDisplayName The display name of the user locking the transcript.
   */
  async lockTranscript(transId: string, userDisplayName?: string): Promise<boolean> {
    const accessToken = await this.getAccessToken();
    const user = this.fronteggAuthService.user;
    const displayName =
      userDisplayName || (user as any)?.customClaims?.display_name || user?.name || '';

    try {
      await this.autoclassifierSdkService.transcriptMetaDataSdk.lockTranscript(
        accessToken,
        transId,
        displayName
      );
      return true;
    } catch (error) {
      console.error('Lock failed for:', transId, error);
      return false;
    }
  }

  /**
   * Unlocks a transcript to allow editing.
   * @param transId The unique identifier of the transcript to unlock.
   */
  async unlockTranscript(transId: string): Promise<boolean> {
    const accessToken = await this.getAccessToken();
    try {
      await this.autoclassifierSdkService.transcriptMetaDataSdk.unlockTranscript(
        accessToken,
        transId
      );
      return true;
    } catch (error) {
      console.error('Unlock failed for:', transId, error);
      return false;
    }
  }

  /**
   * Retrieves the status of a transcript.
   * @param transId The unique identifier of the transcript.
   */
  async getTranscriptStatus(transId: string): Promise<any> {
    const accessToken = await this.getAccessToken();
    return this.autoclassifierSdkService.transcriptMetaDataSdk.getTranscriptStatus(
      accessToken,
      transId
    );
  }

  /**
   * Retrieves the summary of a transcript.
   * @param transId The unique identifier of the transcript.
   */
  async getTranscriptSummary(transId: string): Promise<any> {
    const accessToken = await this.getAccessToken();
    return this.autoclassifierSdkService.transcriptMetaDataSdk.getTranscriptMetadataSummary(
      accessToken,
      transId
    );
  }

  /**
   * Records a download audit event for transcript data.
   * @param transId The unique identifier of the transcript.
   */
  async transcriptDownloadAudit(transId: string): Promise<any> {
    const accessToken = await this.getAccessToken();
    return this.autoclassifierSdkService.transcriptMetaDataSdk.transcriptDownloadAudit(
      accessToken,
      transId
    );
  }

  /**
   * Changes a transcript's type and re-enqueuing extraction with the new type.
   * @param transId The unique identifier of the transcript.
   * @param typeId The new transcript type.
   */
  async changeTranscriptType(transId: string, typeId: number): Promise<any> {
    const accessToken = await this.getAccessToken();
    return this.autoclassifierSdkService.transcriptMetaDataSdk.changeTranscriptType(
      accessToken,
      transId,
      typeId
    );
  }

  /**
   * Enqueues a transcript for processing.
   * @param s3Key The S3 key/path of the uploaded transcript file.
   * @param bucket The S3 bucket where the file is stored.
   * @param tenantId The tenant identifier.
   * @param transcriptMetadataId The transcript metadata record ID.
   */
  async enqueueTranscript(
    s3Key: string,
    bucket: string,
    tenantId: string,
    transcriptMetadataId: string
  ): Promise<any> {
    const accessToken = await this.getAccessToken();
    return this.autoclassifierSdkService.transcriptMetaDataSdk.enqueueTranscript(
      accessToken,
      s3Key,
      bucket,
      tenantId,
      transcriptMetadataId
    );
  }
}
